import { createApi } from '@reduxjs/toolkit/query/react';
import { AxiosProgressEvent } from 'axios';

import { apiSahReconciliations } from '~constants/api';

import { Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import { Reconciliation } from '~support-at-home/models';

export const typePrefix = 'reconciliations';

interface ErrorItem {
  id: string;
  ref: string;
  reason: string;
  details: string;
}

interface FileUploadResponse {
  message?: string;
  counts: { processed: number; unmatched: number; skipped: number; valid: number; needsReview: number };
  errors: ErrorItem[];
  reconciliationId?: string;
}

export const reconciliationsApi = createApi({
  reducerPath: 'reconciliationsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahReconciliations }),
  tagTypes: ['reconciliations', 'client-contributions'],
  endpoints: (builder) => ({
    getReconciliations: builder.query<Reconciliation[], Request | void>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [{ type: 'reconciliations', id: 'LIST' }],
      transformResponse: (payload) => unwrap<Reconciliation[]>(payload),
    }),
    getReconciliation: builder.query<Reconciliation, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}`, method: 'GET', params: rest }),
      providesTags: (_result, _error, { id }) => [{ type: 'reconciliations', id }],
      transformResponse: (payload) => unwrap<Reconciliation>(payload),
    }),
    getClientContributions: builder.query<any, Request | void>({
      query: (params) => ({ url: '/client-contributions', method: 'GET', params }),
      providesTags: [{ type: 'client-contributions', id: 'LIST' }],
      transformResponse: (payload) => unwrap<any>(payload),
    }),
    fileUpload: builder.mutation<FileUploadResponse, { formData: FormData; onProgress?: (prog: number) => void }>({
      query: ({ formData, onProgress }) => ({
        url: '/upload',
        method: 'POST',
        body: formData,
        onUploadProgress: (evt: AxiosProgressEvent) => {
          if (onProgress && evt.total) {
            const percent = Math.round((evt.loaded / evt.total) * 100);
            onProgress(percent);
          }
        },
      }),
      invalidatesTags: (_result, _error) => [{ type: 'reconciliations', id: 'LIST' }],
      transformResponse: (payload: { message?: string; data: FileUploadResponse }) => ({
        ...payload.data,
        message: payload.message,
      }),
    }),
    deleteReconciliation: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({ url: `/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error) => [{ type: 'reconciliations', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetReconciliationsQuery,
  useGetReconciliationQuery,
  useFileUploadMutation,
  useDeleteReconciliationMutation,
  useGetClientContributionsQuery,
} = reconciliationsApi;
